import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { expireSessionApi } from '../services/session.service';

export const WARNING_AT_MS = 9 * 60 * 1000; // 9 minutes
export const END_AT_MS = 10 * 60 * 1000; // 10 minutes

export type SessionEndReason = 'timeout' | 'manual' | null;

function formatMMSS(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function formatSrEnd(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (totalSeconds <= 0) return 'Your session is ending now.';
  if (minutes <= 0) return `Your session will end in ${seconds} seconds.`;
  if (seconds === 0) return `Your session will end in ${minutes} minutes.`;
  return `Your session will end in ${minutes} minutes and ${seconds} seconds.`;
}

export const useSessionStore = defineStore('session', () => {
  // session lifecycle (server cookie-backed)
  const sessionEstablished = ref(false);
  const sessionEstablishedAtMs = ref<number | null>(null);
  const lastActivityAtMs = ref<number | null>(null);

  // UI state

  const isWarningModalVisible = ref(false);
  const ended = ref(false);
  const endReason = ref<SessionEndReason>(null);

  // thresholds
  const warningAtMs = ref<number>(WARNING_AT_MS);
  const endAtMs = ref<number>(END_AT_MS);

  const isSessionActive = computed(() => sessionEstablished.value && !ended.value);

  const countdownWarningMsTick = ref<number>(0);
  const countdownEndMsTick = ref<number>(0);

  const countdownHumanText = ref<string>(''); // "9:12"
  const countdownSrText = ref<string>(''); // verbose

  const sessionEndedPath = ref<string>(''); // verbose

  function setThresholds(next: { warningAtMs?: number; endAtMs?: number }) {
    if (typeof next.warningAtMs === 'number') warningAtMs.value = next.warningAtMs;
    if (typeof next.endAtMs === 'number') endAtMs.value = next.endAtMs;
  }

  function startSession(nowMs: number) {
    sessionEstablished.value = true;
    sessionEstablishedAtMs.value = nowMs;
    lastActivityAtMs.value = nowMs;

    isWarningModalVisible.value = false;
    ended.value = false;
    endReason.value = null;
    sessionEndedPath.value = null;
    updateTick(nowMs);
  }

  async function endSession() {
    sessionEstablished.value = false;
    sessionEstablishedAtMs.value = 0;
    lastActivityAtMs.value = 0;
    isWarningModalVisible.value = false;
    ended.value = true;
    endReason.value = 'timeout';
    updateTick(0);
    return await expireSessionApi();
  }

  function recordActivity(nowMs: number) {
    if (!sessionEstablished.value || ended.value) return;
    lastActivityAtMs.value = nowMs;
    isWarningModalVisible.value = false;

    // update tick values immediately so you see reset instantly
    updateTick(nowMs);
  }

  function showWarning() {
    if (!sessionEstablished.value || ended.value) return;
    isWarningModalVisible.value = true;
  }

  function dismissWarning() {
    isWarningModalVisible.value = false;
  }

  function clearSession(reason: SessionEndReason) {
    sessionEstablished.value = false;
    sessionEstablishedAtMs.value = null;
    lastActivityAtMs.value = null;

    isWarningModalVisible.value = false;

    ended.value = true;
    endReason.value = reason;

    countdownWarningMsTick.value = 0;
    countdownEndMsTick.value = 0;
    countdownHumanText.value = '';
    countdownSrText.value = '';
  }

  function updateTick(nowMs: number) {
    if (!isSessionActive.value || !lastActivityAtMs.value) {
      countdownWarningMsTick.value = 0;
      countdownEndMsTick.value = 0;
      countdownHumanText.value = '';
      countdownSrText.value = '';
      return;
    }

    const sinceActivity = Math.max(0, nowMs - lastActivityAtMs.value);

    countdownWarningMsTick.value = Math.max(0, warningAtMs.value - sinceActivity);
    countdownEndMsTick.value = Math.max(0, endAtMs.value - sinceActivity);

    // keep derived strings aligned with tick values
    countdownHumanText.value = formatMMSS(countdownEndMsTick.value);
    countdownSrText.value = formatSrEnd(countdownEndMsTick.value);
  }

  return {
    // state
    sessionEstablished,
    sessionEstablishedAtMs,
    lastActivityAtMs,
    isWarningModalVisible,
    ended,
    endReason,

    // config
    warningAtMs,
    endAtMs,

    // getters
    isSessionActive,

    // ticking countdown values
    countdownWarningMsTick,
    countdownEndMsTick,

    // strings
    countdownHumanText,
    countdownSrText,

    // actions
    clearSession,
    dismissWarning,
    endSession,
    recordActivity,
    setThresholds,
    showWarning,
    startSession,
    updateTick,
  };
});
